import * as B from '@blueprintjs/core';
import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {
  CartItemRow,
  cartFullMessage,
  cartItemDeepLink,
  cartPageUrl,
  cartTotalCents,
  sceneCountLabel,
  useCart,
  useRemoveWithUndo,
} from 'app/modules/Cart';
import {useAuth} from 'app/providers/AuthProvider';
import colors from 'app/styles/colors.json';
import {formatCost} from 'app/utils/costUtils';
import * as routeUtils from 'app/utils/routeUtils';

import cs from './styles.module.scss';

const EmptyPreview: React.FC = () => (
  <div className={cs.emptyState}>
    <B.Icon icon="shopping-cart" size={24} className={cs.emptyStateIcon} />
    <div className={cs.emptyStateTitle}>Your cart is empty</div>
    <div className={cs.emptyStateHint}>
      Add scenes from a property to review and order them here
    </div>
  </div>
);

const CartPreview: React.FC<{organizationId: string; onGoToCheckout: () => void}> = ({
  organizationId,
  onGoToCheckout,
}) => {
  const {items, isFull, maxItems} = useCart();
  const removeWithUndo = useRemoveWithUndo();

  if (items.length === 0) {
    return (
      <div className={cs.preview}>
        <EmptyPreview />
      </div>
    );
  }

  return (
    <div className={cs.preview}>
      <div className={cs.previewHeader}>
        <span className={cs.previewTitle}>Cart</span>
        <span className={cs.previewCount}>{sceneCountLabel(items.length)}</span>
      </div>

      {isFull && <div className={cs.capNotice}>{cartFullMessage(maxItems)}</div>}

      <div className={cs.items}>
        {items.map((item) => (
          <CartItemRow
            key={item.id}
            item={item}
            className={cs.item}
            deepLinkUrl={cartItemDeepLink(item, organizationId)}
            onRemove={removeWithUndo}
          />
        ))}
      </div>

      <div className={cs.footer}>
        <div className={cs.total}>
          <span>Total</span>
          <span className={cs.totalAmount}>{formatCost(cartTotalCents(items))}</span>
        </div>
        <B.Button intent="primary" fill onClick={onGoToCheckout}>
          Go to checkout
        </B.Button>
      </div>
    </div>
  );
};

/**
 * AppNav badge for the imagery cart. Props-free like PendingRunsIndicator so it can read its own
 * state wherever the nav renders it: AppNav owns the flag and role gate, and this owns the route
 * gate. The preview renders from stored snapshots only — the checkout page owns every refresh.
 */
export const CartIndicator: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const {count, isPreviewOpen, setPreviewOpen} = useCart();
  const {pathname} = useLocation();
  const routeOrgIdPrefix = React.useContext(routeUtils.CurrentOrgIdPrefix);

  // Row deep links navigate from inside the popover, which Blueprint treats as an interior click
  // and leaves open over the page the user just landed on.
  React.useEffect(() => setPreviewOpen(false), [pathname, setPreviewOpen]);

  if (auth.status !== 'logged in') {
    return null;
  }
  const {currentOrganization} = auth;
  if (!currentOrganization) {
    return null;
  }
  // Carts are stored per user and per org, so pages outside an org-scoped route (the org switcher,
  // internal tools) have no cart to count.
  if (!routeOrgIdPrefix) {
    return null;
  }

  const label = count === 0 ? 'Cart is empty' : `Cart, ${sceneCountLabel(count)}`;

  const goToCheckout = () => {
    setPreviewOpen(false);
    navigate(cartPageUrl(currentOrganization.get('id')));
  };

  // Toasts render in their own overlay, which ranks below an already-open popover in Blueprint's
  // stack, so their "View cart" action reads as an outside click: it would close the popover on
  // mousedown just before its own click handler reopened it.
  const onInteraction = (nextIsOpen: boolean, e?: React.SyntheticEvent<HTMLElement>) => {
    const target = e?.target;
    if (
      !nextIsOpen &&
      target instanceof Element &&
      target.closest(`.${B.Classes.TOAST_CONTAINER}`)
    ) {
      return;
    }
    setPreviewOpen(nextIsOpen);
  };

  return (
    <B.Popover
      className={cs.popoverTarget}
      content={
        <CartPreview organizationId={currentOrganization.get('id')} onGoToCheckout={goToCheckout} />
      }
      interactionKind="click"
      position={B.Position.BOTTOM_RIGHT}
      isOpen={isPreviewOpen}
      onInteraction={onInteraction}
      minimal
    >
      <B.Button
        minimal
        className={cs.cartButton}
        icon={<B.Icon icon="shopping-cart" color={colors.primary} />}
        aria-label={label}
        title={label}
      >
        {count > 0 && (
          <B.Tag minimal round className={cs.countTag}>
            {count}
          </B.Tag>
        )}
      </B.Button>
    </B.Popover>
  );
};

export default CartIndicator;
